// src/services/api.js
import axios from "axios";
import { toast } from "react-toastify";

const API_URL = process.env.REACT_APP_BACKEND_URL || "https://210polk.mil.gov.ua/api";
const STORAGE_URL = process.env.REACT_APP_STORAGE_URL || "https://210polk.mil.gov.ua";

axios.defaults.withCredentials = true;

const apiClient = axios.create({
  baseURL: API_URL,
  headers: { "Content-Type": "application/json" },
});

const patronatApiClient = axios.create({
  baseURL: API_URL,
  headers: { "Content-Type": "application/json" },
});

const weekendApiClient = axios.create({
  baseURL: API_URL,
  headers: { "Content-Type": "application/json" },
});

// ─── Token getters + setters ──────────────────────────────────────────────────
let getAccessToken: () => string | null = () => null;
let setAccessToken: (token: string) => void = () => {};
export const registerTokenGetter = (getter: () => string | null) => { getAccessToken = getter; };
export const registerTokenSetter = (setter: (token: string) => void) => { setAccessToken = setter; };

// ─── Refresh state ────────────────────────────────────────────────────────────
// Single shared promise + toast across ALL clients.
// Only one refresh ever runs at a time; concurrent 403s queue on the same promise.
let refreshPromise: Promise<string> | null = null;
let refreshToastId: string | number | null = null;

const REFRESH_URL = "/admin/refresh";

// ─── Core refresh function ────────────────────────────────────────────────────
const refreshAccessToken = (): Promise<string> => {
  if (refreshPromise) return refreshPromise;

  // Show the toast exactly once, here, before the request fires
  refreshToastId = toast.loading("Session expired, refreshing…");

  // Use a raw axios instance (no interceptors) so the refresh call itself
  // can never trigger another 403 retry loop
  refreshPromise = axios
    .post(`${API_URL}${REFRESH_URL}`, {}, { withCredentials: true })
    .then((res) => {
      const newToken = res.data?.accessToken;
      if (!newToken) throw new Error("No access token in refresh response");

      // Persist immediately — all three clients share the same getter/setter
      setAccessToken(newToken);

      toast.update(refreshToastId!, {
        render: "Session refreshed!",
        type: "success",
        isLoading: false,
        autoClose: 2000,
      });

      return newToken;
    })
    .catch((err) => {
      toast.update(refreshToastId!, {
        render: "Session expired. Redirecting to login…",
        type: "error",
        isLoading: false,
        autoClose: 3000,
      });
      setTimeout(() => { window.location.href = "/login"; }, 1500);
      throw err;
    })
    .finally(() => {
      refreshPromise = null;
      refreshToastId = null;
    });

  return refreshPromise;
};

// ─── Interceptor factory ───────────────────────────────────────────────────────
const attachInterceptors = (
  client: ReturnType<typeof axios.create>,
  getToken: () => string | null,
) => {
  // 1. Request — inject current token
  client.interceptors.request.use((config) => {
    const token = getToken();
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  // 2. Response — handle 403 with one shared refresh attempt
  client.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;

      const is403 = error.response?.status === 403;
      // Hard-exclude the refresh endpoint to prevent any loop
      const isRefreshCall = originalRequest.url?.includes(REFRESH_URL);

      if (is403 && !isRefreshCall && !originalRequest._retried) {
        originalRequest._retried = true; // prevent this specific request retrying twice

        try {
          const newToken = await refreshAccessToken();

          // Patch the header and retry via the raw axios so it doesn't
          // pass through this interceptor again
          originalRequest.headers = {
            ...originalRequest.headers,
            Authorization: `Bearer ${newToken}`,
          };
          return axios(originalRequest);
        } catch (refreshError) {
          // Toast + redirect already handled inside refreshAccessToken
          return Promise.reject(refreshError);
        }
      }

      console.error("API Error:", error.response || error);
      return Promise.reject(error);
    },
  );
};

// Thunks ensure closures always read/write the current module-level variables.
attachInterceptors(apiClient,         () => getAccessToken());
attachInterceptors(patronatApiClient, () => getAccessToken());
attachInterceptors(weekendApiClient,  () => getAccessToken());

// ─── Services ──────────────────────────────────────────────────────────────────
export const authService = {
  login:          (password, googleToken) => apiClient.post("/admin/login", { password, googleToken }),
  refresh:        ()                      => apiClient.post("/admin/refresh", {}, { withCredentials: true }),
  logout:         ()                      => apiClient.post("/admin/logout", {}),
  changePassword: (oldPassword: string, newPassword: string) =>
    apiClient.post("/admin/change-password", { oldPassword, newPassword }),
  getEmails:   ()                              => apiClient.get("/admin/emails"),
  addEmail:    (email: string)                 => apiClient.post("/admin/emails", { email }),
  updateEmail: (oldEmail: string, newEmail: string) =>
    apiClient.put(`/admin/emails/${encodeURIComponent(oldEmail)}`, { newEmail }),
  deleteEmail: (email: string) => apiClient.delete(`/admin/emails/${encodeURIComponent(email)}`),
};


export const advantageService = {
  getAll:  ()         => apiClient.get("/advantages"),
  getById: (id)       => apiClient.get(`/advantages/${id}`),
  create:  (data)     => apiClient.post("/advantages", data),
  update:  (id, data) => apiClient.put(`/advantages/${id}`, data),
  delete:  (id)       => apiClient.delete(`/advantages/${id}`),
};

export const battleHistoryService = {
  getAll:  ()         => apiClient.get("/battle-history"),
  getById: (id)       => apiClient.get(`/battle-history/${id}`),
  create:  (data)     => apiClient.post("/battle-history", data),
  update:  (id, data) => apiClient.put(`/battle-history/${id}`, data),
  delete:  (id)       => apiClient.delete(`/battle-history/${id}`),
};

export const socialMediaService = {
  getAll:  ()         => apiClient.get("/social-media"),
  getById: (id)       => apiClient.get(`/social-media/${id}`),
  create:  (data)     => apiClient.post("/social-media", data),
  update:  (id, data) => apiClient.put(`/social-media/${id}`, data),
  delete:  (id)       => apiClient.delete(`/social-media/${id}`),
};

export const videoService = {
  getAll:   ()              => apiClient.get("/videos"),
  getById:  (id)            => apiClient.get(`/videos/${id}`),
  create:   (data)          => apiClient.post("/videos", data),
  update:   (id, data)      => apiClient.put(`/videos/${id}`, data),
  delete:   (id)            => apiClient.delete(`/videos/${id}`),
  reorder:  (id, newOrder)  => apiClient.put(`/videos/reorder/${id}`, { newOrder }),
};

export const anketaService = {
  getAll:     ()     => apiClient.get("/anketa"),
  getById:    (id)   => apiClient.get(`/anketa/${id}`),
  create:     (data) => apiClient.post("/anketa", data),
  delete:     (id)   => apiClient.delete(`/anketa/${id}`),
  markAsRead: (id)   => apiClient.put(`/anketa/${id}/read`),
};

export const seoService = {
  getAll: ()                    => apiClient.get("/seo"),
  update: (page, key, value)    => apiClient.put(`/seo/${page}/${key}`, { value }),
};

export const emailService = {
  send: (data) => apiClient.post("/email/send", data),
};

export const productService = {
  getAll:  ()         => apiClient.get("/products"),
  getById: (id)       => apiClient.get(`/products/${id}`),
  create:  (data)     => apiClient.post("/products", data),
  update:  (id, data) => apiClient.put(`/products/${id}`, data),
  delete:  (id)       => apiClient.delete(`/products/${id}`),
};

export const patronatService = {
  getAll:  ()         => patronatApiClient.get("/patronat"),
  getById: (id)       => patronatApiClient.get(`/patronat/${id}`),
  create:  (data)     => patronatApiClient.post("/patronat", data),
  update:  (id, data) => patronatApiClient.put(`/patronat/${id}`, data),
  delete:  (id)       => patronatApiClient.delete(`/patronat/${id}`),
};

export const imageService = {
  upload: async (file, route = "") => {
    const formData = new FormData();
    formData.append("image", file);
    const query = route ? `?path=${encodeURIComponent(route)}` : "";
    try {
      const response = await apiClient.post(`/images/upload-image${query}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return STORAGE_URL + response.data.path;
    } catch (error) {
      console.error("Error uploading image:", error);
      throw error;
    }
  },
};

export const patronatInfoService = {
  getAll:     ()           => patronatApiClient.get("/patronat-info"),
  getByName:  (name)       => patronatApiClient.get(`/patronat-info/${name}`),
  create:     (data)       => patronatApiClient.post("/patronat-info", data),
  update:     (name, data) => patronatApiClient.put(`/patronat-info/${name}`, data),
  delete:     (name)       => patronatApiClient.delete(`/patronat-info/${name}`),
};

export const mainInfoService = {
  getAll:    ()           => apiClient.get("/main-info"),
  getByName: (name)       => apiClient.get(`/main-info/${name}`),
  create:    (data)       => apiClient.post("/main-info", data),
  update:    (name, data) => apiClient.put(`/main-info/${name}`, data),
  delete:    (name)       => apiClient.delete(`/main-info/${name}`),
};

export const howToHelpService = {
  getAll:  ()         => apiClient.get("/how-to-help"),
  getById: (id)       => apiClient.get(`/how-to-help/${id}`),
  create:  (data)     => apiClient.post("/how-to-help", data),
  update:  (id, data) => apiClient.put(`/how-to-help/${id}`, data),
  delete:  (id)       => apiClient.delete(`/how-to-help/${id}`),
};

export const proPolkService = {
  get:    ()     => apiClient.get("/pro-polk"),
  update: (data) => apiClient.put("/pro-polk", data),
};

export const weekendsService = {
  getAll:  ()         => weekendApiClient.get("/weekends"),
  getById: (id)       => weekendApiClient.get(`/weekends/${id}`),
  create:  (data)     => weekendApiClient.post("/weekends", data),
  // data: { name, slug, status? }
  update:  (id, data) => weekendApiClient.put(`/weekends/${id}`, data),
  delete:  (id)       => weekendApiClient.delete(`/weekends/${id}`),
};

export const weekendInfoService = {
  getAll:    (weekendId)             => weekendApiClient.get(`/weekends/${weekendId}/info`),
  getByName: (weekendId, name)       => weekendApiClient.get(`/weekends/${weekendId}/info/${name}`),
  create:    (weekendId, data)       => weekendApiClient.post(`/weekends/${weekendId}/info`, data),
  update:    (weekendId, name, data) => weekendApiClient.put(`/weekends/${weekendId}/info/${name}`, data),
  delete:    (weekendId, name)       => weekendApiClient.delete(`/weekends/${weekendId}/info/${name}`),
};

export const weekendSkillsService = {
  getAll:   (weekendId)         => weekendApiClient.get(`/weekends/${weekendId}/skills`),
  getById:  (weekendId, id)     => weekendApiClient.get(`/weekends/${weekendId}/skills/${id}`),
  create:   (weekendId, data)   => weekendApiClient.post(`/weekends/${weekendId}/skills`, data),
  update:   (weekendId, id, data) => weekendApiClient.put(`/weekends/${weekendId}/skills/${id}`, data),
  delete:   (weekendId, id)     => weekendApiClient.delete(`/weekends/${weekendId}/skills/${id}`),
  reorder:  (weekendId, id, newOrder) => weekendApiClient.put(`/weekends/${weekendId}/skills/reorder/${id}`, { newOrder }),
};

export const weekendScheduleService = {
  getAll:   (weekendId)         => weekendApiClient.get(`/weekends/${weekendId}/schedule`),
  getById:  (weekendId, id)     => weekendApiClient.get(`/weekends/${weekendId}/schedule/${id}`),
  create:   (weekendId, data)   => weekendApiClient.post(`/weekends/${weekendId}/schedule`, data),
  update:   (weekendId, id, data) => weekendApiClient.put(`/weekends/${weekendId}/schedule/${id}`, data),
  delete:   (weekendId, id)     => weekendApiClient.delete(`/weekends/${weekendId}/schedule/${id}`),
  reorder:  (weekendId, id, newOrder) => weekendApiClient.put(`/weekends/${weekendId}/schedule/reorder/${id}`, { newOrder }),
};

export default {
  advantageService,
  battleHistoryService,
  socialMediaService,
  videoService,
  anketaService,
  emailService,
  productService,
  patronatService,
  imageService,
  patronatInfoService,
  mainInfoService,
  howToHelpService,
  proPolkService,
  weekendsService,
  weekendInfoService,
  weekendScheduleService,
  weekendSkillsService
};